import _ from 'lodash'
import {DateTime} from 'luxon'
import {User} from 'next-auth'

import {BrandSubscriptionStatus} from '@/utils/brand-subscription/constants'
import {brandSubscriptionOrm} from '@/utils/orm-tools/brandSubscription'
import {registrationRequestOrm} from '@/utils/orm-tools/registrationRequest'
import {serviceRequestOrm} from '@/utils/orm-tools/serviceRequest'
import {RegistrationRequestAction} from '@/utils/registration-request/constants'
import {BadRequestError, ForbiddenError} from '@/utils/server/errors'
import {ServiceRequestStatus} from '@/utils/service-request/types'
import {
  AutomaticUserTag,
  SelectableUserTag,
  UserTag,
  userTagOmittanceRules,
} from '@/utils/user/constants'

import {UserRole} from '../auth/constants'
import {
  User_Profile_Select_Column,
  User_Profile_Set_Input,
  User_Select_Column,
  User_Set_Input,
  UserForUserFragment,
  UserFragment,
} from '../graphqlSdk'
import {userOrm} from '../orm-tools/user'
import {userHistoryOrm} from '../orm-tools/userHistory'
import {userProfileOrm} from '../orm-tools/userProfile'
import {getUserExists} from './server'

export const isAdminRole = (user: UserForUserFragment | null) => {
  return user?.role === UserRole.admin
}

export const assertOwnership = <T extends Record<string, any>>(
  entity: T,
  key: keyof T,
  userId: string,
) => {
  if (entity[key] !== userId) {
    throw new ForbiddenError('Entity belongs to a different user', {entity, key, userId})
  }

  return true
}

export const assertOwnershipOrAdmin = <T extends Record<string, any>>(
  entity: T,
  key: keyof T,
  user: User,
) => {
  if (user.role !== UserRole.admin && entity[key] !== user.id) {
    throw new ForbiddenError('Entity belongs to a different user', {entity, key, userId: user.id})
  }

  return true
}

const getUserOrdersCount = async (userId: string): Promise<number> => {
  const paidRegistrationRequestsCount = await registrationRequestOrm.aggregate.count({
    user_id: {_eq: userId},
    tracking_actions: {name: {_eq: RegistrationRequestAction.invoicePaid}},
    invoice: {_not: {credit_note: {}}},
  })

  const paidServiceRequestsCount = await serviceRequestOrm.aggregate.count({
    user_id: {_eq: userId},
    tracking_actions: {name: {_eq: ServiceRequestStatus.paid}},
    invoice: {_not: {credit_note: {}}},
  })

  const brandSubscriptionsCount = await brandSubscriptionOrm.aggregate.count({
    brand: {user_id: {_eq: userId}},
    tracking_actions: {name: {_eq: BrandSubscriptionStatus.subscriptionStarted}},
  })

  return paidRegistrationRequestsCount + paidServiceRequestsCount + brandSubscriptionsCount
}

const getRecentVerificationCount = async (userId: string): Promise<number> => {
  return registrationRequestOrm.aggregate.count({
    user_id: {_eq: userId},
    _not: {tracking_actions: {name: {_eq: RegistrationRequestAction.invoicePaid}}},
    created_at: {_gte: DateTime.now().minus({month: 2}).toISO()},
  })
}

const calculateAutomaticTags = async (userId: string): Promise<AutomaticUserTag[]> => {
  const numberOfOrders = await getUserOrdersCount(userId)
  const numberOfVerifications = await getRecentVerificationCount(userId)

  return [
    ...(numberOfOrders >= 2 ? [AutomaticUserTag.loyalCustomer] : []),
    ...(numberOfVerifications >= 5 && numberOfOrders === 0 ? [AutomaticUserTag.excessiveUse] : []),
  ]
}

export const calculateUserTags = async (user: UserFragment, recalculateAutomaticTags: boolean) => {
  const selectedTags: SelectableUserTag[] = (user.tags ?? []).filter((tag: SelectableUserTag) =>
    _.values(SelectableUserTag).includes(tag),
  )

  const automaticTags: AutomaticUserTag[] = recalculateAutomaticTags
    ? await calculateAutomaticTags(user.id)
    : (user.tags ?? []).filter((tag: AutomaticUserTag) => _.values(AutomaticUserTag).includes(tag))

  const allTags = [...selectedTags, ...automaticTags]
  const omittedTags = _.flatMap(allTags, (tag: UserTag) => userTagOmittanceRules[tag] ?? [])

  return _.reject(allTags, (tag) => omittedTags.includes(tag))
}

export const hasAdaLinTag = (user: Pick<UserFragment, 'tags'>): boolean => {
  return user.tags?.includes(UserTag.adaLin)
}

type UpdateUserData = {
  email: NonNullable<User_Set_Input['email']>
  tags: NonNullable<User_Set_Input['tags']>
}

export const updateUserAllowEmailChange = async (
  id: string,
  userData: UpdateUserData,
  profileData: User_Profile_Set_Input,
  updatedByUserId: string,
): Promise<UserFragment> => {
  const sanitizedUserData = _.pick(userData, [User_Select_Column.Tags, User_Select_Column.Email])
  const sanitizedProfileData = _.pick(profileData, ..._.values(User_Profile_Select_Column))

  const user = await userOrm.getById(id)

  const sanitizedEmail = sanitizedUserData?.email?.toLowerCase()
  if (user.email.toLowerCase() !== sanitizedEmail) {
    const exists = await getUserExists(sanitizedEmail)

    if (exists) {
      throw new BadRequestError('The email address has already been used', {email: user.email})
    }

    const registrationRequests = await registrationRequestOrm.getMany({
      user_id: {_eq: user.id},
    })

    await Promise.all([
      ...registrationRequests.map(async (registrationRequest) =>
        registrationRequestOrm.updateOne(registrationRequest.id, {
          contact_email: sanitizedUserData.email,
        }),
      ),
      userHistoryOrm.insertOne({
        email: user.email,
        user_id: user.id,
        created_by_user_id: updatedByUserId,
      }),
    ])
  }

  const userProfile = await userProfileOrm.getOne({user_id: {_eq: id}})
  await userProfileOrm.updateOne(userProfile.id, sanitizedProfileData)
  return await userOrm.updateOne(
    id,
    {
      ...sanitizedUserData,
      // recalculate tags, new selected tags can contain some with additional rules
      tags: await calculateUserTags({...user, ...sanitizedUserData}, false),
    },
    {allowEmailChange: true},
  )
}
